import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from '@nwwp/ui-shadcn/lib/utils'

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        /** NWWP primary CTA — white pill, matches MUI theme Button (contained default) */
        cta: 'bg-white text-black font-semibold uppercase rounded-full shadow-none hover:bg-white/80 active:bg-white/80',
        /** NWWP secondary CTA — dark pill with white text */
        'cta-dark':
          'bg-foreground text-background font-semibold uppercase rounded-full shadow-none hover:bg-foreground/90 active:bg-foreground/90',
        /** NWWP funnel CTA — greenLighter2 pill, used in calculator/funnel flows */
        funnel:
          'bg-green-lighter-2 text-black font-semibold uppercase rounded-full shadow-none hover:bg-green-lighter active:bg-green-lighter disabled:opacity-100 disabled:text-neutral-darker disabled:bg-neutral-light',
        /** NWWP match variant — blue background, used for match/partner CTAs */
        match: 'bg-blue text-white font-semibold uppercase rounded-full shadow-none hover:bg-blue/90 active:bg-blue/80',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
        /** NWWP CTA pill size — matches MUI theme: 16px font, 27.5px padding on mobile, 87.5px on tablet+ */
        cta: 'h-14 px-[27.5px] tablet:px-[87.5px] text-base',
        'cta-sm': 'py-4 px-8 text-body-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Button({
  className,
  variant = 'default',
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : 'button'
  const resolvedSize = size ?? (variant === 'funnel' ? 'cta' : 'default')

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={resolvedSize}
      className={cn(buttonVariants({ variant, size: resolvedSize, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
